/*
 * Sportsact: mængderabat-vælger (snippets/custom-quantity-breaks.liquid).
 * - Det valgte trin skriver antallet i det skjulte felt name="quantity" i produktformularen.
 * - Priserne regnes fra variantens pris: antal × pris − rabat (samme regel som Shopifys automatiske rabatter).
 * - Variant pr. stk.: vælger kunden forskellige varianter, sendes de som flere linjer (items[]) i stedet for id + antal.
 * - totalCents() bruges af assets/custom-atc-price.js til prisen i "Tilføj til kurv" og sticky-baren.
 */
(() => {
  if (customElements.get('custom-quantity-breaks')) return;

  class CustomQuantityBreaks extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      try {
        this.data = JSON.parse(this.querySelector('[data-qb-data]').textContent);
      } catch (e) {
        return;
      }

      this.formId = this.getAttribute('form');
      this.form = document.getElementById(this.formId);
      this.quantityInput = this.querySelector('[data-qb-quantity]');
      this.variantId = this.form?.querySelector('[name="id"]')?.value;

      this.addEventListener('change', (event) => {
        if (event.target.matches('.custom-qb__radio')) this.select();
        if (event.target.matches('[data-qb-select]')) this.notify();
      });

      this.form?.addEventListener('variant:change', (event) => {
        const variant = event.detail.variant;
        this.variantId = variant?.id ?? this.variantId;
        this.hidden = !variant || !variant.available;
        this.resetSelects();
        this.render();
      });

      // Forskellige varianter pr. stk.: send som flere linjer
      this.form?.addEventListener('formdata', (event) => this.onFormData(event.formData));

      this.select();
    }

    get radio() {
      return this.querySelector('.custom-qb__radio:checked');
    }

    select() {
      const radio = this.radio;
      if (!radio || !this.quantityInput) return;
      this.quantityInput.value = radio.dataset.quantity;
      this.render();
    }

    notify() {
      this.form?.dispatchEvent(new CustomEvent('quantity-breaks:change', { bubbles: true }));
    }

    resetSelects() {
      this.querySelectorAll('[data-qb-select]').forEach((select) => {
        const option = select.querySelector(`option[value="${this.variantId}"]`);
        if (option && !option.disabled) select.value = String(this.variantId);
      });
    }

    // Varianterne for det valgte trin (ét id pr. stk.)
    selectedVariantIds() {
      const radio = this.radio;
      const quantity = parseInt(radio?.dataset.quantity, 10) || 1;
      const selects = [...(radio?.closest('.custom-qb__option')?.querySelectorAll('[data-qb-select]') || [])];
      if (selects.length === quantity) return selects.map((select) => select.value);
      return Array(quantity).fill(String(this.variantId));
    }

    // Samlet pris for det valgte trin i øre (null hvis ukendt)
    totalCents(radio = this.radio, ids = null) {
      if (!radio || this.hidden) return null;
      ids = ids || (radio === this.radio ? this.selectedVariantIds() : Array(parseInt(radio.dataset.quantity, 10) || 1).fill(String(this.variantId)));

      // Shopify giver rabatten pr. linje: grupper ens varianter
      const lines = {};
      ids.forEach((id) => { lines[id] = (lines[id] || 0) + 1; });

      let total = 0;
      for (const [id, count] of Object.entries(lines)) {
        const variant = this.data.variants[id];
        if (!variant) return null;
        const full = variant.price * count;
        const value = parseFloat(radio.dataset.value) || 0;
        total += full - (radio.dataset.type === 'kr' ? Math.round(value * 100 * count) : Math.round((full * value) / 100));
      }
      return total;
    }

    format(cents) {
      const hasDecimals = cents % 100 !== 0;
      const amount = new Intl.NumberFormat('da-DK', { minimumFractionDigits: hasDecimals ? 2 : 0, maximumFractionDigits: 2 }).format(cents / 100);
      return (this.data.money_format || '{{amount}} kr').replace(/\{\{\s*\w+\s*\}\}/, amount);
    }

    render() {
      const variant = this.data.variants[String(this.variantId)];
      if (!variant) return;
      const unitCompare = Math.max(variant.price, variant.compare || 0);

      // Trin med flere stk., end der er på lager, kan ikke vælges
      const radios = [...this.querySelectorAll('.custom-qb__radio')];
      radios.forEach((radio) => {
        radio.disabled = variant.max !== null && variant.max !== undefined && (parseInt(radio.dataset.quantity, 10) || 1) > variant.max;
      });
      if (this.radio?.disabled || !this.radio) {
        const fallback = radios.find((radio) => !radio.disabled) || radios[0];
        if (fallback) {
          fallback.disabled = false;
          fallback.checked = true;
          if (this.quantityInput) this.quantityInput.value = fallback.dataset.quantity;
        }
      }

      this.querySelectorAll('.custom-qb__radio').forEach((radio) => {
        const option = radio.closest('.custom-qb__option');
        const quantity = parseInt(radio.dataset.quantity, 10) || 1;
        const total = this.totalCents(radio, Array(quantity).fill(String(this.variantId)));
        const compare = unitCompare * quantity;
        const saving = compare - total;

        const totalNode = option.querySelector('[data-qb-total]');
        const compareNode = option.querySelector('[data-qb-compare]');
        const savingNode = option.querySelector('[data-qb-saving]');

        if (totalNode) totalNode.textContent = this.format(total);
        if (compareNode) {
          compareNode.textContent = this.format(compare);
          compareNode.hidden = compare <= total;
        }
        if (savingNode) {
          savingNode.textContent = (this.data.save || '__AMOUNT__').replace('__AMOUNT__', this.format(saving));
          savingNode.hidden = saving <= 0;
        }
      });

      this.notify();
    }

    onFormData(formData) {
      if (this.hidden || !this.radio) return;
      const ids = this.selectedVariantIds();
      if (ids.every((id) => id === String(this.variantId))) return; // almindelig id + antal

      const lines = {};
      ids.forEach((id) => { lines[id] = (lines[id] || 0) + 1; });

      formData.delete('id');
      formData.delete('quantity');
      Object.entries(lines).forEach(([id, count], index) => {
        formData.append(`items[${index}][id]`, id);
        formData.append(`items[${index}][quantity]`, count);
      });
    }
  }

  customElements.define('custom-quantity-breaks', CustomQuantityBreaks);
})();
